import { useState } from "react";
import { Edit2, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { apiClient } from "../../App";
import { toast } from "sonner";

export function ProgramContactStrip({ program, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    client_name: program?.client_name || "",
    client_email: program?.client_email || "",
    client_phone: program?.client_phone || "",
    project_address_full: program?.project_address_full || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.patch(`/programs/${program.id}/contact`, form);
      toast.success("Contact info updated");
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Failed to update contact info");
    }
    setSaving(false);
  };

  const googleMapsUrl = form.project_address_full 
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.project_address_full)}`
    : null;

  if (editing) {
    return (
      <div className="rounded-2xl border border-border p-4 space-y-4" data-testid="contact-strip-edit">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">Client Name</label>
            <Input 
              value={form.client_name} 
              onChange={(e) => setForm({...form, client_name: e.target.value})}
              placeholder="Client Name"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">Email</label>
            <Input 
              value={form.client_email} 
              onChange={(e) => setForm({...form, client_email: e.target.value})}
              placeholder="client@email.com"
              type="email"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">Phone</label>
            <Input 
              value={form.client_phone} 
              onChange={(e) => setForm({...form, client_phone: e.target.value})}
              placeholder="+1 (555) 123-4567"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">Project Address</label>
            <Input 
              value={form.project_address_full} 
              onChange={(e) => setForm({...form, project_address_full: e.target.value})}
              placeholder="123 Main St, City, State 12345"
            />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(false)}
            disabled={saving}
            className="h-9 rounded-full px-4 text-[13px] text-muted-foreground hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="h-9 rounded-full bg-foreground px-4 text-[13px] font-medium text-background hover:bg-foreground/90"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    );
  }

  // One quiet typographic line: name in foreground, the rest muted until
  // hovered. Whitespace does the separating — no icons, and no dot separators
  // (a wrapped line would start with a stray "·").
  const fields = [];
  if (program?.client_name) {
    fields.push(
      <span key="name" className="font-medium text-foreground">
        {program.client_name}
      </span>
    );
  }
  if (program?.client_email) {
    fields.push(
      <a
        key="email"
        href={`mailto:${program.client_email}`}
        className="hover:text-foreground transition-colors"
      >
        {program.client_email}
      </a>
    );
  }
  if (program?.client_phone) {
    fields.push(
      <a
        key="phone"
        href={`tel:${program.client_phone}`}
        className="hover:text-foreground transition-colors"
      >
        {program.client_phone}
      </a>
    );
  }
  if (program?.project_address_full && googleMapsUrl) {
    fields.push(
      <a
        key="address"
        href={googleMapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex max-w-[260px] items-center gap-1 hover:text-foreground transition-colors"
      >
        <span className="truncate">{program.project_address_full}</span>
        <ExternalLink className="w-3 h-3 shrink-0 opacity-60" />
      </a>
    );
  }

  return (
    <div
      className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] text-muted-foreground"
      data-testid="contact-strip"
    >
      {fields.length === 0 ? (
        <button
          onClick={() => setEditing(true)}
          className="text-[13px] text-muted-foreground hover:text-foreground transition-colors"
          data-testid="contact-strip-add"
        >
          + Add client details
        </button>
      ) : (
        <>
          {fields}
          <button
            onClick={() => setEditing(true)}
            className="-ml-2 rounded-full p-1 text-muted-foreground/60 hover:bg-secondary hover:text-foreground transition-colors"
            title="Edit client details"
            data-testid="contact-strip-edit-btn"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
        </>
      )}
    </div>
  );
}
